import { useState, useCallback } from 'react';
import { Connection, PublicKey, Transaction, type TransactionInstruction } from '@solana/web3.js';
import { useDynamicContext } from '@dynamic-labs/sdk-react-core';
import { isSolanaWallet } from '@dynamic-labs/solana';

import * as srslyLegacy from '@sly-rentals/core/legacy';
import { useNetwork } from '../lib/network-context';
import { HAS_DYNAMIC_ENV } from '../components/WalletProvider';
import type { Instruction } from '../data/instructions.generated';

export type RunnerStatus =
  | { kind: 'idle' }
  | { kind: 'running' }
  | { kind: 'success'; signature: string; newAddress?: string }
  | { kind: 'error'; message: string; code?: string };

import type { FetchStatus } from '../components/playground/Console';

interface UseTransactionRunnerResult {
  status: RunnerStatus;
  canRun: boolean;
  reason?: string;
  run: (instruction: Instruction, values: Record<string, string>) => Promise<void>;
  reset: () => void;
  /** Fetch + decode an account by address. Used by the unified Console. */
  fetchAccount: (address: string, accountType: string) => Promise<FetchStatus>;
  /** Fetch + decode every account of a given type (program-derived list). */
  fetchAllAccounts: (accountType: string) => Promise<FetchStatus>;
}

// Acct-name → SDK fetchAll wrapper. Pluralization is irregular enough (configs,
// not configes; borrowers, not borrowers's) that a hand-written map is clearer
// than a string-mash. Keys are the lowercase `acct` field on FetchEntry.
const FETCH_ALL_FNS = {
  borrower: 'fetchAllBorrowers',
  config: 'fetchAllConfigs',
  contract: 'fetchAllContracts',
  rental: 'fetchAllRentals',
  member: 'fetchAllMembers',
} as const;

export const FETCH_ALL_SUPPORTED: ReadonlySet<string> = new Set(Object.keys(FETCH_ALL_FNS));

// Convert a JS-style object/array literal (the form examples are written
// like `{ atlas: 100 }`, `{ lamports: 50_000_000 }`) into a JSON string we
// can `JSON.parse`. We:
//  - strip numeric underscores (50_000_000 → 50000000)
//  - quote unquoted object keys
//  - convert single-quoted strings to double-quoted
//  - drop trailing commas before } or ]
function jsLiteralToJson(raw: string): string {
  return raw
    .replace(/(\d)_(?=\d)/g, '$1')
    .replace(/([{,]\s*)([A-Za-z_$][\w$]*)\s*:/g, '$1"$2":')
    .replace(/'((?:[^'\\]|\\.)*)'/g, '"$1"')
    .replace(/,(\s*[}\]])/g, '$1');
}

// Coerce raw form-input strings to JS values the SDK wrapper expects. Strings
// that parse as JSON (or relaxed JS-literal form) are passed through —
// lets the user paste shapes like `{ atlas: 100 }` directly. Otherwise
// treat as plain string, number, or boolean as appropriate.
function coerce(raw: string): unknown {
  const trimmed = raw.trim();
  if (trimmed === '') return undefined;
  if (trimmed === 'true') return true;
  if (trimmed === 'false') return false;
  if (trimmed === 'null') return null;
  if (/^-?\d[\d_]*(\.\d+)?$/.test(trimmed)) {
    return Number(trimmed.replace(/_/g, ''));
  }
  if (/^[{[]/.test(trimmed) || /^["']/.test(trimmed)) {
    try {
      return JSON.parse(trimmed);
    } catch {
      try {
        return JSON.parse(jsLiteralToJson(trimmed));
      } catch {
        return trimmed; // fall through to plain string
      }
    }
  }
  return trimmed;
}

function buildParams(
  instruction: Instruction,
  values: Record<string, string>
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const p of instruction.params) {
    const v = coerce(values[p.name] ?? '');
    if (v === undefined && p.optional) continue;
    out[p.name] = v;
  }
  return out;
}

export function useTransactionRunner(): UseTransactionRunnerResult {
  const [status, setStatus] = useState<RunnerStatus>({ kind: 'idle' });
  const { rpcUrl, rpcReady, network } = useNetwork();
  const { primaryWallet } = useDynamicContext();

  const reset = useCallback(() => setStatus({ kind: 'idle' }), []);

  let canRun = true;
  let reason: string | undefined;
  if (!rpcReady) {
    // Previously this fell through to a preset for a cluster the bar does not
    // offer, so runs silently targeted the wrong chain.
    canRun = false;
    reason = 'No RPC URL set — enter a custom RPC URL in the network bar.';
  } else if (!HAS_DYNAMIC_ENV) {
    canRun = false;
    reason = 'Set DYNAMIC_ENVIRONMENT_ID in playground/.env to enable Try-it.';
  } else if (!primaryWallet) {
    canRun = false;
    reason = 'Connect a wallet first.';
  } else if (!isSolanaWallet(primaryWallet)) {
    canRun = false;
    reason = 'Active wallet is not a Solana wallet.';
  }

  const run = useCallback(
    async (instruction: Instruction, values: Record<string, string>) => {
      if (!canRun || !primaryWallet || !isSolanaWallet(primaryWallet)) {
        setStatus({ kind: 'error', message: reason ?? 'Cannot run.' });
        return;
      }

      setStatus({ kind: 'running' });
      try {
        // Use the legacy entry point so we get web3.js TransactionInstructions
        // back instead of @solana/kit instructions, which Dynamic's signer
        // contract expects today.
        const fnName = instruction.name as keyof typeof srslyLegacy;
        const fn = srslyLegacy[fnName];
        if (typeof fn !== 'function') {
          throw new Error(`SDK does not export legacy wrapper for "${instruction.name}".`);
        }

        const params = buildParams(instruction, values);
        // Thread the network-bar RPC into the wrapper's config so its
        // build-time reads (getMinimumBid, resolveContractSnapshot,
        // fetchFleet, resolveReferrer, …) hit the selected cluster instead
        // of falling back to the SDK singleton / mainnet-beta default.
        // @ts-expect-error dispatched dynamically — params shape varies per ix
        const result = await fn(params, { rpcUrl });
        const ixs: TransactionInstruction[] = Array.isArray(result) ? result : [result];

        // Pull the post-tx "result account" address out of the built
        // instruction so the Console can offer a one-click fetch
        // afterwards. Some wrappers (createContract with `automated`)
        // bundle multiple SRSLY instructions plus a compute-budget ix,
        // so we identify the target by matching its expected account
        // count from the IDL — assumed unique within a single bundle.
        let newAddress: string | undefined;
        if (instruction.resultAccount) {
          const { accountIndex, keysCount } = instruction.resultAccount;
          const programIx = ixs.find(ix => ix.keys.length === keysCount);
          const meta = programIx?.keys[accountIndex];
          newAddress = meta?.pubkey?.toBase58();
        }

        // Build, sign, and broadcast all on the user-selected RPC. Dynamic's
        // `signAndSendTransaction` would broadcast against its own internal
        // connection (typically mainnet), so we use `signTransaction` to get
        // the signed bytes back and submit them ourselves — that way a user
        // who picked atlasnet/devnet/custom actually lands the tx there.
        const connection = new Connection(rpcUrl, 'confirmed');
        const { blockhash, lastValidBlockHeight } =
          await connection.getLatestBlockhash('confirmed');
        const tx = new Transaction({
          feePayer: new PublicKey(primaryWallet.address),
          blockhash,
          lastValidBlockHeight,
        });
        tx.add(...ixs);

        const signer = await primaryWallet.getSigner();
        // Casts through `unknown` because Dynamic's signer ships its own
        // bundled @solana/web3.js Transaction class — the literal types
        // differ from ours, but the runtime layout is wire-compatible.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const signed = (await signer.signTransaction(tx as any)) as unknown as Transaction;
        const rawTx = signed.serialize() as Uint8Array;
        const signature = await connection.sendRawTransaction(rawTx, {
          skipPreflight: false,
          preflightCommitment: 'confirmed',
        });
        await connection.confirmTransaction(
          { signature, blockhash, lastValidBlockHeight },
          'confirmed'
        );

        setStatus({ kind: 'success', signature, newAddress });
      } catch (err) {
        setStatus({
          kind: 'error',
          message: err instanceof Error ? err.message : String(err),
        });
      }
    },
    [canRun, primaryWallet, reason, rpcUrl, network]
  );

  // Decode an account using the SDK's fetch* legacy wrapper. Account-type is
  // the lower-case bare name (`contract`, `rental`, `borrower`, etc.); we
  // construct `fetchContract` / `fetchRental` / etc. dynamically so the
  // Console doesn't need to know the SDK function name per group.
  const fetchAccount = useCallback(
    async (address: string, accountType: string): Promise<FetchStatus> => {
      try {
        const cap = accountType.charAt(0).toUpperCase() + accountType.slice(1);
        const fnName = `fetch${cap}` as keyof typeof srslyLegacy;
        const fn = srslyLegacy[fnName];
        if (typeof fn !== 'function') {
          return {
            kind: 'error',
            message: `SDK does not export ${String(fnName)}().`,
          };
        }
        // @ts-expect-error dispatched dynamically — fetch* signatures vary
        const data = await fn(address.trim(), rpcUrl);
        if (data == null) return { kind: 'notfound' };
        // Replace BigInts so JSON renderer can handle them.
        const safe = JSON.parse(
          JSON.stringify(data, (_k, v) => (typeof v === 'bigint' ? v.toString() : v))
        );
        return { kind: 'ok', data: safe };
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        if (/not found|does not exist/i.test(msg)) return { kind: 'notfound' };
        return { kind: 'error', message: msg };
      }
    },
    [rpcUrl]
  );

  // Fetch every account of `accountType` via the SDK's program-derived fetchAll
  // helper. Returns the raw array as `data` so the existing FetchResult JSON
  // renderer can pretty-print it with no special-casing.
  const fetchAllAccounts = useCallback(
    async (accountType: string): Promise<FetchStatus> => {
      try {
        const fnName = FETCH_ALL_FNS[accountType as keyof typeof FETCH_ALL_FNS];
        if (!fnName) {
          return {
            kind: 'error',
            message: `No fetchAll wrapper for "${accountType}".`,
          };
        }
        const fn = srslyLegacy[fnName as keyof typeof srslyLegacy];
        if (typeof fn !== 'function') {
          return {
            kind: 'error',
            message: `SDK does not export ${fnName}().`,
          };
        }
        // @ts-expect-error dispatched dynamically — fetchAll* take optional rpcUrl
        const data = await fn(rpcUrl);
        const safe = JSON.parse(
          JSON.stringify(data, (_k, v) => (typeof v === 'bigint' ? v.toString() : v))
        );
        return { kind: 'ok', data: safe };
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        // The default mainnet RPC (api.mainnet-beta.solana.com) and most other
        // free public endpoints disable getProgramAccounts. Detect the common
        // failure shapes and surface an actionable hint instead of the raw
        // Solana error #8100002 / 403 wall.
        if (
          /\b(8100002|410|403)\b/.test(msg) ||
          /HTTP error/i.test(msg) ||
          /getProgramAccounts/i.test(msg)
        ) {
          return {
            kind: 'error',
            message:
              'The selected RPC rejected the program scan. ' +
              'Public mainnet endpoints (e.g. api.mainnet-beta.solana.com) disable ' +
              'getProgramAccounts. Switch to a custom RPC that supports it ' +
              '(Helius, Triton, QuickNode, or your own validator) and retry.',
          };
        }
        return { kind: 'error', message: msg };
      }
    },
    [rpcUrl]
  );

  return { status, canRun, reason, run, reset, fetchAccount, fetchAllAccounts };
}
